import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { inr, px } from "../lib/api";
import { useStore } from "../context/StoreContext";
import EditableImage from "./admin/EditableImage";
import EditableText from "./admin/EditableText";

export default function ProductCard({ product, index = 0, onQuickView, onProductUpdate }) {
  const { toggleWishlist, inWishlist } = useStore();
  const [hover, setHover] = useState(false);
  const [prod, setProd] = useState(product);
  const saved = inWishlist(prod.slug);

  const updateProduct = async (patch) => {
    // Spread the full product then apply patch — server ignores extras.
    const payload = {
      ...prod,
      color: (prod.colors && prod.colors[0]) || "",
      ...patch,
    };
    const { data } = await api.put(`/admin/products/${prod.id}`, payload);
    setProd((prev) => ({ ...prev, ...data }));
    onProductUpdate && onProductUpdate({ ...prod, ...data });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`product-card-${prod.slug}`}
    >
      <div
        className="product-media relative aspect-[3/4] overflow-hidden bg-[#efece6]"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <EditableImage
          src={px(prod.primary_image, 700)}
          onSave={async (url) => {
            await updateProduct({ primary_image: url });
            toast.success("Product image updated");
          }}
          label={`Product · ${prod.name}`}
          alt={prod.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full"
          imgClassName="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: hover ? 0 : 1, transform: hover ? "scale(1.04)" : "scale(1)" }}
        />
        <Link to={`/product/${prod.slug}`} className="absolute inset-0 z-0" aria-label={prod.name}>
          <img
            src={px(prod.hover_image || prod.primary_image, 700)}
            alt={prod.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
            style={{ opacity: hover ? 1 : 0, transform: hover ? "scale(1.04)" : "scale(1.08)" }}
          />
        </Link>

        {/* badges */}
        <div className="absolute left-3 top-3 flex flex-col gap-1.5 pointer-events-none">
          {prod.is_new && (
            <span className="bg-ivory/90 px-2.5 py-1 overline text-ink">New</span>
          )}
          {prod.on_sale && (
            <span className="bg-ink px-2.5 py-1 overline text-ivory">Sale</span>
          )}
          {(() => {
            const sbs = prod.stock_by_size;
            const hasSizeStock = sbs && Object.keys(sbs).length > 0;
            const hasFlatStock = prod.stock !== undefined && prod.stock !== null;
            // Only compute sold-out when we actually have stock data — avoids a
            // false "Sold out" badge on cards (e.g. recently viewed) missing stock.
            if (!hasSizeStock && !hasFlatStock) return null;
            const total = hasSizeStock
              ? Object.values(sbs).reduce((a, b) => a + Number(b || 0), 0)
              : Number(prod.stock || 0);
            return total <= 0 ? (
              <span className="bg-red-600/90 px-2.5 py-1 overline text-white" data-testid={`sold-out-${prod.slug}`}>Sold out</span>
            ) : null;
          })()}
        </div>

        {/* wishlist */}
        <button
          onClick={() => toggleWishlist(prod)}
          className="absolute right-3 top-3 z-20 grid h-9 w-9 place-items-center rounded-full bg-ivory/85 text-ink backdrop-blur-sm transition-colors hover:bg-ink hover:text-ivory"
          data-testid={`wishlist-toggle-${prod.slug}`}
          aria-label="Toggle wishlist"
          style={{ marginRight: 0 }}
        >
          <Heart size={15} fill={saved ? "currentColor" : "none"} />
        </button>

        {/* quick view */}
        <motion.button
          onClick={() => onQuickView && onQuickView(prod)}
          className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-center gap-2 bg-ivory/90 py-3 overline text-ink backdrop-blur-sm"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: hover ? 1 : 0, y: hover ? 0 : 12 }}
          transition={{ duration: 0.35 }}
          data-testid={`quick-view-${prod.slug}`}
        >
          <Eye size={14} /> Quick View
        </motion.button>
      </div>

      <div className="pt-4">
        <p className="overline text-gold-deep mb-1">{prod.fabric}</p>
        <EditableText
          as="h3"
          value={prod.name}
          onSave={async (v) => {
            await updateProduct({ name: v });
            toast.success("Product name updated");
          }}
          label="Product Name"
          className="font-serif text-xl leading-tight hover:text-gold-deep transition-colors"
        >
          <Link to={`/product/${prod.slug}`}>{prod.name}</Link>
        </EditableText>
        <div className="mt-1.5 flex items-center gap-3">
          <EditableText
            as="span"
            value={prod.price}
            inputType="number"
            onSave={async (v) => {
              await updateProduct({ price: Number(v) });
              toast.success("Price updated");
            }}
            label="Price (₹)"
            className="text-sm"
          >
            {inr(prod.price)}
          </EditableText>
          {prod.compare_at_price && (
            <span className="text-sm text-ink-soft line-through">{inr(prod.compare_at_price)}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
